import { real_id, shellyId16 } from 'src/helpers/interface_helpers';

/**
 * Canonical hex id used in `slotAssignments` and `$devices` keys.
 * Decimal BE ids become padded hex; hex ids and channel suffixes
 * (`id_0`) collapse to the same value.
 */
export function normalizeCameraSlotDeviceId(id: string): string {
	if (!id) return '';
	return shellyId16(real_id(String(id)));
}

export function cameraSlotIdEquals(a: string, b: string): boolean {
	if (!a || !b) return false;
	if (a === b) return true;
	return normalizeCameraSlotDeviceId(a) === normalizeCameraSlotDeviceId(b);
}

/**
 * Drop matching device ids from an occupied-slot list, keeping order.
 * Used after delete / factory-reset / transfer so a camera that left
 * the account (or this home) does not keep occupying a slot.
 */
export function dropCamerasFromOccupiedSlotIds(
	occupiedIds: readonly string[],
	deviceIds: readonly string[],
): string[] {
	if (!deviceIds.length) return [...occupiedIds];
	return occupiedIds.filter(
		(assignedId) => !deviceIds.some((id) => cameraSlotIdEquals(assignedId, id)),
	);
}

/** First candidate id that is not already occupying a Cloud Storage slot. */
export function getFirstUnassignedCameraId(
	candidateIds: readonly string[],
	occupiedIds: readonly string[],
): string | undefined {
	return candidateIds.find(
		(id) => !!id && !occupiedIds.some((occupied) => cameraSlotIdEquals(occupied, id)),
	);
}

const CAMERA_SLOT_LIST_STORAGE_PREFIX = 'shelly.ccs_camera_list:';

const cameraSlotListStorageKey = (email: string): string =>
	`${CAMERA_SLOT_LIST_STORAGE_PREFIX}${email.trim().toLowerCase()}`;

/** Persist the account's occupied slot ids so sub-unit sessions can still show them. */
export function persistCameraSlotListForAccount(email: string, ids: readonly string[]): void {
	if (!email || typeof localStorage === 'undefined') return;
	const next = ids.filter(Boolean);
	if (next.length === 0) return;
	try {
		localStorage.setItem(cameraSlotListStorageKey(email), JSON.stringify(next));
	} catch {
		// Private mode / quota — slot display on sub-units just won't survive reload.
	}
}

export function readPersistedCameraSlotListForAccount(email: string): string[] | null {
	if (!email || typeof localStorage === 'undefined') return null;
	try {
		const raw = localStorage.getItem(cameraSlotListStorageKey(email));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		return parsed.filter((id): id is string => typeof id === 'string' && !!id);
	} catch {
		return null;
	}
}

export function clearPersistedCameraSlotListForAccount(email: string): void {
	if (!email || typeof localStorage === 'undefined') return;
	try {
		localStorage.removeItem(cameraSlotListStorageKey(email));
	} catch {
		// ignore
	}
}
